/**
 * Returns a safe in-app path for post-login redirect, or null if untrusted.
 * Blocks open redirects and common bypasses used in pen tests:
 * protocol-relative URLs, backslashes, encoded //, control chars, schemes.
 */
export function resolveSafePostLoginPath(path) {
    if (!path || typeof path !== 'string') return null;

    let candidate = path.trim();
    if (!candidate) return null;

    // Reject absolute / protocol URLs before decode (and after, below).
    if (looksLikeExternalOrUnsafePath(candidate)) return null;

    try {
        candidate = decodeURIComponent(candidate);
    } catch {
        return null;
    }

    candidate = candidate.trim();
    if (looksLikeExternalOrUnsafePath(candidate)) return null;

    // Only same-origin relative paths starting with a single "/".
    if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;

    // Disallow backslash (\\evil), null bytes, and other control characters.
    if (hasControlCharOrBackslash(candidate)) return null;

    // Disallow scheme-like segments (javascript:, data:, etc.).
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate.slice(1))) return null;

    // Keep path length bounded to avoid abuse via oversized redirects.
    if (candidate.length > 2048) return null;

    return candidate;
}

function hasControlCharOrBackslash(candidate) {
    for (let i = 0; i < candidate.length; i += 1) {
        const code = candidate.charCodeAt(i);
        if (code <= 0x1f || code === 0x7f || code === 0x5c) return true;
    }
    return false;
}

function looksLikeExternalOrUnsafePath(value) {
    const lower = value.toLowerCase();
    return (
        lower.startsWith('//') ||
        lower.startsWith('\\\\') ||
        /^[a-z][a-z0-9+.-]*:/i.test(value) ||
        value.includes('\\')
    );
}
